import React from 'react';
import { motion } from 'framer-motion';
import { FiShoppingCart, FiTrendingUp, FiCheckCircle, FiArrowRight, FiBox, FiSettings, FiRefreshCw, FiPackage } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const ShopifyPage = () => {
  useDocumentTitle(
    'High-Converting Shopify Stores | Professional Shopify Development Company',
    'Build a fast, clean, and high-converting online store with support from a certified shopify development partner and a simple, reliable commerce agency focused on real results.'
  );
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-green-600 via-green-700 to-teal-800">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl font-bold text-white mb-6">
                Shopify Development
              </h1>
              <p className="text-2xl text-white/90 mb-8 leading-relaxed">
                Shopify Development helps you start and grow an online store without the technical headaches. We build Shopify stores that look professional, work smoothly, and help you sell more
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-green-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  <span>Launch Your Store</span>
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-green-600 transition-all duration-300"
                >
                  <span>View Our Work</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Shopify Development */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">What is Shopify Development?</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Shopify development is a simple and effective way to get your online store up and running without needing to be a tech expert. Shopify is a platform that handles all the hard stuff—hosting, security, payments—so you can focus on selling. As the best Shopify development company in India, we design and set up your Shopify store to look professional, work smoothly, and help you convert visitors into paying customers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiShoppingCart className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Store Setup & Design</h3>
              <p className="text-gray-600 leading-relaxed">
                We design and build your Shopify store from scratch—beautiful, easy to navigate, and ready to sell
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiSettings className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Product & Payment Setup</h3>
              <p className="text-gray-600 leading-relaxed">
                We add your products, set up payment gateways, and make sure everything works perfectly before you launch
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiPackage className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Custom Features & Apps</h3>
              <p className="text-gray-600 leading-relaxed">
                Need something specific? We add custom features, apps, and integrations to make your store work exactly how you need it to
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiRefreshCw className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Ongoing Support & Updates</h3>
              <p className="text-gray-600 leading-relaxed">
                We stay with you after launch—keeping your store updated, secure, and running smoothly
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our Shopify Development Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From concept to profitable online store
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-8">
            {[
              {
                step: '01',
                title: 'Strategy & Planning',
                description: 'Understand your products, target market, and business goals. Create a comprehensive e-commerce strategy and store architecture.',
                icon: FiBox
              },
              {
                step: '02',
                title: 'Design & Branding',
                description: 'Design a beautiful, conversion-optimized store that reflects your brand and provides seamless shopping experience.',
                icon: FiShoppingCart
              },
              {
                step: '03',
                title: 'Development & Setup',
                description: 'Build your Shopify store with custom features, configure products, collections, and integrate payment gateways.',
                icon: FiSettings
              },
              {
                step: '04',
                title: 'Product Migration',
                description: 'Add or migrate your products with optimized descriptions, high-quality images, and proper categorization.',
                icon: FiPackage
              },
              {
                step: '05',
                title: 'Testing & Optimization',
                description: 'Comprehensive testing of checkout process, payment systems, shipping calculations, and mobile responsiveness.',
                icon: FiCheckCircle
              },
              {
                step: '06',
                title: 'Launch & Training',
                description: 'Launch your store and provide complete training on managing products, orders, and marketing tools.',
                icon: FiTrendingUp
              }
            ].map((process, index) => (
              <div key={index} className="flex gap-6 items-start group">
                <div className="flex-shrink-0">
                  <div className="bg-gradient-to-br from-green-500 to-teal-600 text-white text-2xl font-bold w-16 h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300">
                    {process.step}
                  </div>
                </div>
                <div className="flex-grow bg-gray-50 rounded-xl p-6 group-hover:shadow-xl transition-all duration-300">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{process.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Shopify Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Launch your online store with confidence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Starter',
                price: '₹30,000',
                duration: 'one-time',
                popular: false,
                features: [
                  'Shopify Theme Setup',
                  'Up to 50 Products',
                  'Payment Gateway',
                  'Basic Customization',
                  'Mobile Responsive',
                  '1 Month Support'
                ]
              },
              {
                name: 'Growth',
                price: '₹60,000',
                duration: 'one-time',
                popular: true,
                features: [
                  'Custom Theme Design',
                  'Up to 200 Products',
                  'Multiple Payment Options',
                  'Advanced Features',
                  'App Integrations (3)',
                  'SEO Optimization',
                  'Email Setup',
                  '3 Months Support'
                ]
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                duration: 'pricing',
                popular: false,
                features: [
                  'Shopify Plus',
                  'Unlimited Products',
                  'Custom App Development',
                  'API Integrations',
                  'Multi-currency',
                  'Advanced Analytics',
                  '6 Months Support',
                  'Dedicated Manager'
                ]
              }
            ].map((pkg, index) => (
              <div 
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300 ${
                  pkg.popular ? 'ring-4 ring-green-500 shadow-2xl' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{pkg.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-800">{pkg.price}</span>
                  <span className="text-gray-600 ml-2">{pkg.duration}</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <FiCheckCircle className="text-green-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Our Shopify Development?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert e-commerce developers focused on your success
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiShoppingCart className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">We build stores that actually sell</h3>
              <p className="text-gray-600 leading-relaxed">
                Not just pretty designs—we create Shopify stores designed to turn visitors into paying customers
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTrendingUp className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">We handle everything—you focus on selling</h3>
              <p className="text-gray-600 leading-relaxed">
                From setup to launch and beyond, we take care of all the technical stuff so you can focus on your products and customers
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-green-500 to-teal-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiSettings className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Your store stays updated and secure</h3>
              <p className="text-gray-600 leading-relaxed">
                We provide ongoing support, updates, and maintenance to keep your store running smoothly and securely
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Common questions about Shopify development
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "Why Shopify over other e-commerce platforms?",
                answer: "Shopify is the most user-friendly and comprehensive e-commerce platform with built-in hosting, security, payment processing, and inventory management. It's scalable, requires no technical maintenance, and has 8,000+ apps for any functionality you need."
              },
              {
                question: "How long does it take to build a Shopify store?",
                answer: "Basic stores can be launched in 2-3 weeks, while custom stores with advanced features take 4-8 weeks. Migration from other platforms typically takes 3-5 weeks depending on the number of products and complexity."
              },
              {
                question: "What are Shopify's monthly costs?",
                answer: "Shopify plans start at $39/month (Basic) to $399/month (Advanced). Plus transaction fees unless using Shopify Payments. We'll help you choose the right plan based on your sales volume and feature requirements."
              },
              {
                question: "Can you migrate my existing store to Shopify?",
                answer: "Yes! We specialize in migrations from WooCommerce, Magento, BigCommerce, and other platforms. We'll transfer all your products, customers, orders, and maintain your SEO rankings with proper redirects."
              },
              {
                question: "Do I need Shopify Plus?",
                answer: "Shopify Plus ($2,000+/month) is for high-volume merchants doing $1M+ annually. It offers advanced features, wholesale channel, and dedicated support. Most businesses start with regular Shopify plans and upgrade when needed."
              },
              {
                question: "Can you integrate with my existing tools?",
                answer: "Yes! Shopify integrates with thousands of tools including email marketing (Mailchimp, Klaviyo), shipping (ShipStation), accounting (QuickBooks), ERP systems, and custom APIs. We'll connect all your essential tools."
              },
              {
                question: "Will my store be mobile-friendly?",
                answer: "Absolutely! All our Shopify stores are fully responsive and optimized for mobile shopping. With 70%+ of e-commerce traffic from mobile devices, mobile optimization is a top priority in our development process."
              },
              {
                question: "What payment methods can I accept?",
                answer: "Shopify supports 100+ payment gateways including credit cards, PayPal, Apple Pay, Google Pay, and local payment methods. We recommend Shopify Payments for best rates and no transaction fees."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-green-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-green-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Launch Your Shopify Store?</h2>
          <p className="text-2xl mb-12 text-white/90 max-w-3xl mx-auto">
            Let's build a high-converting e-commerce store that grows your business
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-green-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
            >
              <span>Start Your Store</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-green-600 transition-all duration-300"
            >
              <span>View Success Stories</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShopifyPage;
